import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import { useEffect, useState, lazy, Suspense } from "react";
import { useTranslation } from "react-i18next";
import { HelmetProvider } from "react-helmet-async";
import Index from "./pages/Index";
// Lazy-load secondary route pages so their JS (and dependencies like Contacto's
// form/floating-ui chunks) is not part of the initial bundle.
// Auto-recover from stale chunk references after a redeploy: if a dynamic
// import fails (old hashed file no longer exists on the server), reload once.
const lazyWithRetry = <T,>(factory: () => Promise<{ default: React.ComponentType<T> }>) =>
  lazy(() =>
    factory().catch((err) => {
      const KEY = "lovable-chunk-reload";
      if (typeof window !== "undefined" && !sessionStorage.getItem(KEY)) {
        sessionStorage.setItem(KEY, "1");
        window.location.reload();
        return new Promise<never>(() => {});
      }
      throw err;
    })
  );

const Plataforma = lazyWithRetry(() => import("./pages/Plataforma"));
const Soluciones = lazyWithRetry(() => import("./pages/Soluciones"));
const SolucionDetalle = lazyWithRetry(() => import("./pages/SolucionDetalle"));

const Industrias = lazyWithRetry(() => import("./pages/Industrias"));
const IndustriaDetalle = lazyWithRetry(() => import("./pages/IndustriaDetalle"));
const Recursos = lazyWithRetry(() => import("./pages/Recursos"));
const Blog = lazyWithRetry(() => import("./pages/Blog"));
const BlogArticle = lazyWithRetry(() => import("./pages/BlogArticle"));
const RegulacionDetalle = lazyWithRetry(() => import("./pages/RegulacionDetalle"));
const Nosotros = lazyWithRetry(() => import("./pages/Nosotros"));
const Contacto = lazyWithRetry(() => import("./pages/Contacto"));
const Privacy = lazyWithRetry(() => import("./pages/Privacy"));
const Terms = lazyWithRetry(() => import("./pages/Terms"));
const Cookies = lazyWithRetry(() => import("./pages/Cookies"));
const DataProtection = lazyWithRetry(() => import("./pages/DataProtection"));

const NotFound = lazyWithRetry(() => import("./pages/NotFound"));
const Unsubscribe = lazyWithRetry(() => import("./pages/Unsubscribe"));
const ChatBot = lazyWithRetry(() => import("./components/ChatBot"));
const FloatingWhatsApp = lazyWithRetry(() => import("./components/FloatingWhatsApp"));
const CookieConsent = lazyWithRetry(() => import("./components/CookieConsent"));
import { loadGoogleAnalytics, trackPageView } from "@/lib/analytics";

const queryClient = new QueryClient();

const SUPPORTED_LANGS = ["es", "en", "pt"] as const;
type SupportedLang = (typeof SUPPORTED_LANGS)[number];
const isSupportedLang = (l: string | undefined): l is SupportedLang =>
  !!l && (SUPPORTED_LANGS as readonly string[]).includes(l);

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    trackPageView(pathname);
  }, [pathname]);
  return null;
};

const LanguageWrapper = ({ children }: { children: React.ReactNode }) => {
  const { lang } = useParams<{ lang: string }>();
  const { i18n } = useTranslation();

  useEffect(() => {
    if (isSupportedLang(lang) && i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [lang, i18n]);

  useEffect(() => {
    document.documentElement.lang = i18n.language;
    document.documentElement.setAttribute("translate", "no");
  }, [i18n.language]);

  return <>{children}</>;
};

const LocalizedRoutes = () => {
  const { lang } = useParams<{ lang: string }>();
  const { pathname, search, hash } = useLocation();

  // If the first segment isn't a supported language, redirect to /es/<original-path>
  if (!isSupportedLang(lang)) {
    return <Navigate to={`/es${pathname}${search}${hash}`} replace />;
  }

  return (
    <LanguageWrapper>
      <Suspense fallback={null}>
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/plataforma" element={<Plataforma />} />
          <Route path="/soluciones" element={<Soluciones />} />
          <Route path="/soluciones/:slug" element={<SolucionDetalle />} />

          <Route path="/industrias" element={<Industrias />} />
          <Route path="/industrias/:slug" element={<IndustriaDetalle />} />
          <Route path="/recursos" element={<Recursos />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogArticle />} />
          <Route path="/regulaciones/:slug" element={<RegulacionDetalle />} />
          <Route path="/regulaciones" element={<Navigate to="/recursos" replace />} />
          <Route path="/nosotros" element={<Nosotros />} />
          <Route path="/contacto" element={<Contacto />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/cookies" element={<Cookies />} />
          <Route path="/proteccion-datos" element={<DataProtection />} />
          <Route path="/politicas-de-privacidad" element={<Navigate to={`/${lang}/proteccion-datos`} replace />} />

          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </LanguageWrapper>
  );
};

/**
 * Defers mounting non-critical UI (ChatBot, CookieConsent) until the browser
 * is idle or after a short timeout. This removes ~40KB+ of JS (markdown, etc.)
 * from the critical path, improving FCP/LCP/TBT on mobile.
 */
const DeferredUI = () => {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const trigger = () => { if (!cancelled) setReady(true); };

    const w = window as Window & { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number };
    if (typeof w.requestIdleCallback === "function") {
      w.requestIdleCallback(trigger, { timeout: 4000 });
    } else {
      const id = window.setTimeout(trigger, 3000);
      return () => { cancelled = true; clearTimeout(id); };
    }
    return () => { cancelled = true; };
  }, []);

  if (!ready) return null;

  return (
    <Suspense fallback={null}>
      <ChatBot />
      <FloatingWhatsApp />
      <CookieConsent />
    </Suspense>
  );
};

const App = () => {
  // Try loading GA on mount in case the user already gave consent in a previous visit
  useEffect(() => {
    loadGoogleAnalytics();
  }, []);

  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <ScrollToTop />
            <Suspense fallback={null}>
              <Routes>
                <Route path="/" element={<Navigate to="/es" replace />} />
                <Route path="/unsubscribe" element={<Unsubscribe />} />
                <Route path="/:lang/*" element={<LocalizedRoutes />} />
              </Routes>
            </Suspense>
            <DeferredUI />
          </BrowserRouter>
        </TooltipProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
};

export default App;
